import { useTranslation } from 'react-i18next';

import useAiChatToolLabel from '../../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import AiChatAnalysisResult from '../AiChatAnalysisResult';
import AiChatToolCard from '../AiChatToolCard';

import type { ReactElement } from 'react';
import type { IAiChatToolCallOf } from '../../interfaces/IAiChatToolCall';

/**
 * The `startImpactAnalysis` tool call: a run of an impact analysis, started from the chat.
 *
 * The backend started the run. When the part arrives live, `useAiChatToolActions` opens the
 * map's Impact Scenarios panel on the analysis and has the panel follow the run by its id. This
 * row draws the part — its counts on one open line, nothing to press — and under it the run
 * itself: the same result card the panel shows, in progress or done, kept in step with the panel
 * through the cache they share. From history it is the same row and the same card, and nothing
 * opens.
 */
function StartImpactAnalysisToolCall({ part }: IAiChatToolCallOf<'startImpactAnalysis'>): ReactElement {
  const { t } = useTranslation();
  const label = useAiChatToolLabel('startImpactAnalysis');
  const status = useAiChatToolStatus(part.state);

  const output = part.state === 'output-available' ? part.output : undefined;

  const readSummary = (): string | undefined => {
    if (!output) {
      return status;
    }

    if (!output.ok) {
      return t('aiChat.tools.impactAnalysisRefused');
    }

    // An action the FE does not know opened the panel and followed nothing; see `useAiChatToolActions`.
    return t(
      (output.action as string) === 'runStarted' ? 'aiChat.tools.impactRunStarted' : 'aiChat.tools.impactPanelOpened',
      { name: output.name },
    );
  };

  // Under the header: the reason of a refusal, or which analysis while the call runs. Once the
  // call is done the header names the analysis itself.
  const readNote = (): string | undefined => {
    if (output) {
      return output.ok ? undefined : output.reason;
    }

    return part.input?.analysisId
      ? t('aiChat.tools.impactAnalysisFallbackName', { id: part.input.analysisId })
      : undefined;
  };

  const isRunStarted = output?.ok && (output.action as string) === 'runStarted';

  const content = output?.ok && isRunStarted ? (
    <AiChatAnalysisResult
      valueChainMapId={output.valueChainMapId}
      analysisId={output.analysisId}
      name={output.name}
      resultId={output.resultId}
    />
  ) : undefined;

  const details = output?.ok ? (
    <p>
      {t('aiChat.tools.impactRunCounts', {
        scenarios: t('aiChat.tools.impactScenarioCount', { count: output.scenarioCount }),
        units: t('aiChat.tools.impactUnitCount', { count: output.unitCount }),
      })}
    </p>
  ) : undefined;

  return (
    <AiChatToolCard
      name={label}
      status={readSummary()}
      content={content}
      details={details}
      isDetailsAlwaysOpen
      errorText={part.errorText}
    >
      {readNote()}
    </AiChatToolCard>
  );
}

export default StartImpactAnalysisToolCall;
